import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Clock, Calendar, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventReminders({ eventId, event }) {
  const [sending24h, setSending24h] = useState(false);
  const [sending30min, setSending30min] = useState(false);

  const sendReminder = async (reminderType) => {
    const setter = reminderType === '24h' ? setSending24h : setSending30min;
    setter(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/events/${eventId}/send-reminders?reminder_type=${reminderType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Reminder inviati a ${response.data.recipients_count} partecipanti!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dei reminder');
    } finally {
      setter(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Email Reminder Automatici
        </CardTitle>
        <p className="text-sm text-gray-600">
          Invia promemoria ai partecipanti via email con link all'evento
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">📧 Cosa Viene Inviato</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>✅ Dettagli evento (titolo, data, luogo)</li>
            <li>✅ Link biglietto personale con QR code</li>
            <li>✅ Link streaming (se evento online/ibrido)</li>
            <li>✅ Pulsante "Aggiungi al calendario"</li>
          </ul>
        </div>

        {/* 24h Reminder */}
        <div className="border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Reminder 24 ore prima</h4>
                <p className="text-sm text-gray-600">
                  "L'evento è domani! Preparati per {event?.title}"
                </p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => sendReminder('24h')}
            disabled={sending24h}
            className="w-full bg-orange-600 hover:bg-orange-700"
          >
            {sending24h ? (
              'Invio in corso...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Invia Reminder 24h
              </>
            )}
          </Button>
        </div>

        {/* 30min Reminder */}
        <div className="border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Reminder 30 minuti prima</h4>
                <p className="text-sm text-gray-600">
                  "Inizia tra 30 minuti! Link streaming pronto"
                </p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => sendReminder('30min')}
            disabled={sending30min}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {sending30min ? (
              'Invio in corso...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Invia Reminder 30min
              </>
            )}
          </Button>
        </div>

        {/* Note */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            <strong>Nota:</strong> Le email vengono inviate a tutti i possessori di biglietti 
            attivi per questo evento. Configura IONOS SMTP nelle impostazioni per l'invio automatico.
          </p>
        </div>

        {/* Future: Automatic Scheduling */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-700 mb-2">🚀 Prossimamente: Invio Automatico</h4>
          <p className="text-sm text-gray-600">
            I reminder verranno inviati automaticamente 24h e 30min prima dell'evento 
            senza bisogno di premere alcun pulsante.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
